/**
 * PlatformSystemHealth — Super-admin data integrity dashboard.
 *
 * Reports drift across Rooms / Snapshots / Hotels / Guests / Reservations
 * with a per-room eligibility trace tool (the "why is this room not
 * assignable?" debugger for Front Office / Support).
 */
import { useCallback, useEffect, useState } from "react";
import { PmsConsistencyCard } from "./PmsConsistencyCard";
import { toast } from "sonner";
import { Activity, RefreshCw, ShieldAlert, Search, Wrench, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const Stat = ({ label, value, tone = "neutral", testid }) => {
  const toneCls = tone === "danger"
    ? "text-rose-600 bg-rose-50 border-rose-200"
    : tone === "warning"
    ? "text-amber-700 bg-amber-50 border-amber-200"
    : tone === "success"
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-mono font-semibold mt-0.5">{value}</div>
    </div>
  );
};

const Section = ({ title, icon: Icon, danger, children, testid }) => (
  <Card className={`p-5 mb-4 ${danger ? "border-rose-200" : ""}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-4 h-4 ${danger ? "text-rose-600" : "text-slate-600"}`} />
      <h3 className="font-semibold text-slate-900">{title}</h3>
      {danger && <Badge variant="destructive" className="ml-auto">Attention</Badge>}
    </div>
    {children}
  </Card>
);

export default function PlatformSystemHealth() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [roomId, setRoomId] = useState("");
  const [eligibility, setEligibility] = useState(null);
  const [tracing, setTracing] = useState(false);

  const [normalising, setNormalising] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system-health");
      setReport(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load system health"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runRepair = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/system-health/repair/snapshot-drift");
      toast.success(
        `Drift migration: ${data.created} rooms reconstructed · `
        + `${data.skipped_existing} already OK · `
        + `${data.skipped_unparseable_ids} unparseable`
      );
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
    finally { setRepairing(false); }
  };

  const runNormalise = async () => {
    setNormalising(true);
    try {
      const { data } = await api.post("/admin/system-health/repair/normalise-references");
      const touched = Object.values(data || {}).reduce(
        (a, coll) => a + Object.values(coll).reduce((b, n) => b + n, 0), 0);
      if (touched === 0) toast.success("Reference IDs already normalised — nothing to do.");
      else toast.success(`Reference normalisation: ${touched} field(s) rewritten to string form.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Normalisation failed"); }
    finally { setNormalising(false); }
  };

  const traceRoom = async () => {
    if (!roomId) return;
    setTracing(true); setEligibility(null);
    try {
      const { data } = await api.get(`/admin/system-health/rooms/${roomId}/eligibility`);
      setEligibility(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Room not found"); }
    finally { setTracing(false); }
  };

  const R = report || {};
  const rooms = R.rooms || {};
  const snap = R.snapshots || {};
  const hotels = R.hotels || {};
  const guests = R.guests || {};
  const res = R.reservations || {};

  const drift = snap.orphan_count || 0;
  const missingSnap = snap.rooms_missing_snapshot || 0;
  const orphanRoomRefs = res.orphan_room_reference_count || 0;

  return (
    <div data-testid="platform-system-health-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-amber-600" /> System Health
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Data integrity across master collections. Green = healthy · Amber = drift · Red = intervention needed.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="sys-health-refresh"
            variant="outline"
            disabled={loading}
            onClick={load}
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            data-testid="sys-health-repair-drift"
            disabled={repairing || drift === 0}
            onClick={runRepair}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Wrench className={`w-4 h-4 mr-1.5 ${repairing ? "animate-spin" : ""}`} />
            {drift === 0 ? "No drift — nothing to repair" : `Repair Drift (${drift})`}
          </Button>
          <Button
            data-testid="sys-health-normalise-refs"
            variant="outline"
            disabled={normalising}
            onClick={runNormalise}
            title="Rewrite any ObjectId-form reference fields to string form (idempotent)"
          >
            <Wrench className={`w-4 h-4 mr-1.5 ${normalising ? "animate-spin" : ""}`} />
            Normalise References
          </Button>
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label="Rooms" value={rooms.total ?? "—"} testid="stat-rooms" />
        <Stat label="Snapshots" value={snap.total ?? "—"} testid="stat-snapshots" />
        <Stat label="Hotels" value={hotels.total ?? "—"} testid="stat-hotels" />
        <Stat label="Guests" value={guests.total ?? "—"} testid="stat-guests" />
        <Stat label="Reservations" value={res.total ?? "—"} testid="stat-reservations" />
      </div>

      <PmsConsistencyCard />

      {/* Rooms ↔ Snapshots drift */}
      <Section
        title="Rooms ↔ Snapshots Drift"
        icon={ShieldAlert}
        danger={drift > 0 || missingSnap > 0 || snap.unparseable_id_count > 0}
        testid="sys-health-drift"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Orphan snapshots" value={drift}
                tone={drift > 0 ? "danger" : "success"}
                testid="stat-orphan-snapshots" />
          <Stat label="Rooms missing snapshot" value={missingSnap}
                tone={missingSnap > 0 ? "warning" : "success"}
                testid="stat-missing-snap" />
          <Stat label="Unparseable snapshot IDs" value={snap.unparseable_id_count || 0}
                tone={snap.unparseable_id_count ? "danger" : "success"}
                testid="stat-unparseable" />
          <Stat label="Reconstructed rooms" value={rooms.reconstructed_from_snapshot || 0}
                testid="stat-reconstructed" />
        </div>
        {drift === 0 && missingSnap === 0 && (
          <div className="mt-3 text-sm text-emerald-700 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Master rooms and snapshots are fully reconciled.
          </div>
        )}
        {snap.orphan_snapshots_without_master_room?.length > 0 && (
          <details className="mt-3" data-testid="orphan-snapshots-list">
            <summary className="text-sm font-medium text-slate-700 cursor-pointer">
              {drift} orphan snapshot(s) — click to inspect
            </summary>
            <div className="mt-2 max-h-56 overflow-y-auto text-xs font-mono bg-slate-50 rounded p-2 space-y-1">
              {snap.orphan_snapshots_without_master_room.slice(0, 100).map((o, i) => (
                <div key={i}>· hotel {o.hotel_id} · room {o.room_number} · id {o.room_id}</div>
              ))}
            </div>
          </details>
        )}
      </Section>

      {/* Reservations integrity */}
      <Section
        title="Reservations"
        icon={ShieldAlert}
        danger={orphanRoomRefs > 0 || res.missing_code > 0}
        testid="sys-health-reservations"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Missing code" value={res.missing_code || 0}
                tone={res.missing_code ? "warning" : "success"}
                testid="stat-res-missing-code" />
          <Stat label="Missing hotel_id" value={res.missing_hotel_id || 0}
                tone={res.missing_hotel_id ? "danger" : "success"}
                testid="stat-res-missing-hotel" />
          <Stat label="Missing dates" value={res.missing_dates || 0}
                tone={res.missing_dates ? "danger" : "success"}
                testid="stat-res-missing-dates" />
          <Stat label="Orphan room refs" value={orphanRoomRefs}
                tone={orphanRoomRefs ? "danger" : "success"}
                testid="stat-res-orphan-rooms" />
        </div>
        {res.orphan_room_references?.length > 0 && (
          <details className="mt-3" data-testid="orphan-room-refs-list">
            <summary className="text-sm font-medium text-slate-700 cursor-pointer">
              {orphanRoomRefs} orphan reservation(s) — click to inspect
            </summary>
            <div className="mt-2 max-h-56 overflow-y-auto text-xs font-mono bg-slate-50 rounded p-2 space-y-1">
              {res.orphan_room_references.map((o, i) => (
                <div key={i}>· {o.code || o.reservation_id} → room {o.assigned_room_id} ({o.reason})</div>
              ))}
            </div>
          </details>
        )}
      </Section>

      {/* Hotels & Guests */}
      <Section title="Hotels & Guests" icon={ShieldAlert} testid="sys-health-hotels-guests">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Hotels missing code" value={hotels.missing_code || 0}
                tone={hotels.missing_code ? "warning" : "success"} />
          <Stat label="Hotels missing is_active" value={hotels.missing_is_active_field || 0}
                tone={hotels.missing_is_active_field ? "warning" : "success"} />
          <Stat label="Guests missing name" value={guests.missing_name || 0}
                tone={guests.missing_name ? "warning" : "success"} />
          <Stat label="Guests missing hotel_id" value={guests.missing_hotel_id || 0}
                tone={guests.missing_hotel_id ? "danger" : "success"} />
        </div>
      </Section>

      {/* Rooms duplicates */}
      {rooms.duplicate_room_numbers?.length > 0 && (
        <Section title="Duplicate Room Numbers" icon={ShieldAlert} danger testid="sys-health-dup-rooms">
          <div className="text-xs font-mono bg-rose-50 rounded p-2 space-y-1 max-h-56 overflow-y-auto">
            {rooms.duplicate_room_numbers.map((d, i) => (
              <div key={i}>· hotel {d.hotel_id} · room {d.room_number} · {d.count} copies</div>
            ))}
          </div>
        </Section>
      )}

      {/* Per-room eligibility trace */}
      <Section title="Room Eligibility Trace" icon={Search} testid="sys-health-eligibility">
        <div className="flex gap-2 mb-3">
          <Input
            data-testid="eligibility-room-id"
            placeholder="Paste room_id (ObjectId) to trace..."
            value={roomId}
            onChange={(e) => setRoomId(e.target.value.trim())}
            className="font-mono text-sm"
          />
          <Button
            data-testid="eligibility-trace-btn"
            disabled={!roomId || tracing}
            onClick={traceRoom}
          >
            <Search className={`w-4 h-4 mr-1.5 ${tracing ? "animate-pulse" : ""}`} /> Trace
          </Button>
        </div>
        {eligibility && (
          <div data-testid="eligibility-result">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-lg font-bold">Room {eligibility.room_number || eligibility.room_id}</div>
              <Badge variant={eligibility.eligible ? "default" : "destructive"}>
                {eligibility.result}
              </Badge>
              <span className="text-[11px] text-slate-500 ml-auto font-mono">
                source: {eligibility.source?.master_rooms ? "master" : ""}{eligibility.source?.master_rooms && eligibility.source?.snapshot ? "+" : ""}{eligibility.source?.snapshot ? "snapshot" : ""}
              </span>
            </div>
            <div className="space-y-1">
              {eligibility.checks.map((c, i) => (
                <div key={i}
                     data-testid={`eligibility-check-${i}`}
                     className="flex items-center gap-2 text-sm">
                  {c.ok
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    : <XCircle className="w-4 h-4 text-rose-600 shrink-0" />}
                  <span className="font-medium min-w-[110px]">{c.label}</span>
                  <span className={c.ok ? "text-slate-700" : "text-rose-700"}>{c.detail}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
